import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { S25ContactProfileImageComponent } from "../contact/contact.profile.image.component";

@TypeManagerDecorator("se-contact-tile")
@Component({
    selector: "se-contact-tile",
    template: `
        <div class="contact-header">
            @if (header) {
                <h3 class="contact-tile-header">{{ header }}</h3>
            }
            @if (showPrimaryBadge) {
                <div class="primary-badge">Primary</div>
            }
        </div>

        <div class="contact-content">
            <s25-ng-contact-profile-image [contactId]="contactId" [name]="name" />

            <div class="contact-details">
                <h4 class="contact-name">{{ name }}</h4>
                @if (title) {
                    <p class="contact-title">{{ title }}</p>
                }
                @if (email) {
                    <p class="contact-email">
                        <s25-ng-icon [type]="'mailFilled'" [size]="18" [ariaLabel]="false" />
                        <a [href]="'mailto:' + email">{{ email }}</a>
                    </p>
                }
                @if (phone) {
                    <p class="contact-phone">
                        <s25-ng-icon [type]="'phone'" [size]="18" [ariaLabel]="false" />
                        <a [href]="'tel:' + phone">{{ phone }}</a>
                    </p>
                }
            </div>
        </div>
    `,
    styles: `
        :host {
            background: var(--color--surface-bg);
            border-radius: 16px;
            padding: 0;
            color: var(--color--text);
            box-shadow: 0 4px 20px color-mix(in srgb, var(--color--text) 8%, transparent);
            border: 1px solid var(--color--border);
            transition:
                transform var(--transition-medium) ease,
                box-shadow var(--transition-medium) ease;
            overflow: hidden;
            position: relative;
            display: block;
        }

        :host:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 30px color-mix(in srgb, var(--color--text) 12%, transparent);
        }

        .contact-header {
            background: linear-gradient(135deg, var(--cn-navy) 0%, var(--cn-navy-700) 100%);
            color: var(--color--surface-bg);
            padding: 1.25rem 1.5rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
            position: relative;
        }

        .contact-header::after {
            content: "";
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 1px;
            background: color-mix(in srgb, var(--color--surface-bg) 20%, transparent);
        }

        .contact-tile-header {
            margin: 0;
            font-size: 1.1rem;
            font-weight: 600;
            color: white;
            letter-spacing: 0.5px;
        }

        .primary-badge {
            background: color-mix(in srgb, var(--color--surface-bg) 20%, transparent);
            color: var(--color--surface-bg);
            padding: 0.4rem 0.8rem;
            border-radius: 20px;
            font-size: 0.75rem;
            font-weight: 600;
            backdrop-filter: blur(10px);
            border: 1px solid color-mix(in srgb, var(--color--surface-bg) 30%, transparent);
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        .contact-content {
            padding: 1.5rem;
            display: flex;
            gap: 1.25rem;
            align-items: flex-start;
        }

        .contact-details {
            flex: 1;
            min-width: 0;
        }

        .contact-name {
            margin: 0 0 0.5rem 0;
            font-size: 1.3rem;
            font-weight: 600;
            color: var(--color--text-strong);
            line-height: 1.2;
        }

        .contact-title {
            margin: 0 0 1.25rem 0;
            color: var(--color--text-muted);
            font-style: italic;
            font-size: 0.95rem;
            font-weight: 400;
        }

        .contact-email,
        .contact-phone {
            margin: 0.75rem 0;
            display: flex;
            align-items: center;
            gap: 0.75rem;
            color: var(--color--text-muted);
            padding: 0.5rem 0;
        }

        .contact-email a,
        .contact-phone a {
            color: var(--cn-blue);
            text-decoration: none;
            font-weight: 500;
            transition: color var(--transition-medium) ease;
        }

        .contact-email a:hover,
        .contact-phone a:hover {
            color: var(--color--link-text-hover);
            text-decoration: underline;
        }

        ::ng-deep .contact-email svg,
        ::ng-deep .contact-phone svg {
            opacity: 0.7;
            color: var(--color--text-muted);
        }

        @media (max-width: 480px) {
            .contact-header {
                padding: 1rem;
            }

            .contact-tile-header {
                font-size: 1rem;
            }

            .primary-badge {
                padding: 0.3rem 0.6rem;
                font-size: 0.7rem;
            }

            .contact-content {
                flex-wrap: wrap;
                padding: 1rem;
                gap: 0.75rem;
            }

            .contact-details {
                display: contents;
            }

            .contact-name {
                font-size: 1.1rem;
                flex: 1;
                align-self: center;
            }

            .contact-title {
                width: 100%;
                font-size: 0.85rem;
                margin: 0;
                text-align: center;
            }

            .contact-email,
            .contact-phone {
                width: 100%;
                justify-content: center;
                font-size: 0.9rem;
                padding: 0.25rem 0;
                margin: 0;
            }

            .contact-email a,
            .contact-phone a {
                word-break: break-all;
            }
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25IconComponent, S25ContactProfileImageComponent],
})
export class SeContactTileComponent {
    @Input() name: string;
    @Input() email?: string;
    @Input() phone?: string;
    @Input() title?: string;
    @Input() imageUrl?: string;
    @Input() contactId?: number;
    @Input() showPrimaryBadge: boolean = false;
    @Input() header?: string;
}
